import { useCallback } from "react";
import { usePathname } from "next/navigation";
import useAnalytics from "../../../analytics/useAnalytics";
import { cleverTapCommonEvents } from "../../../analytics/clevertapConstants";
import { buildCustomizationCleverTapPayload } from "../../../utils/clevertapCommon/customization";

/**
 * CleverTap helpers for PDP customization events (Customize Now Clicked / Customization Done).
 *
 * @param {Object} params
 * @param {Object} [params.baseCleverTapData] - Shared PDP CleverTap fields.
 * @param {Object} [params.currentItem]
 * @param {boolean} [params.isOutOfStock]
 * @param {boolean} [params.isSameSku]
 * @param {boolean} [params.isInternational]
 * @param {Object} [params.deliveryDates]
 * @param {Object} [params.nextAvailableDifference]
 * @param {boolean} [params.isSmartBuyUser]
 * @param {string} [params.productUrl]
 * @returns {{
 *   trackCustomizeNowClicked: () => void,
 *   trackCustomizationDone: () => void,
 *   buildPayload: (overrides?: Object) => Object,
 * }}
 */
const useCustomizationCleverTap = ({
  baseCleverTapData,
  currentItem,
  isOutOfStock = false,
  isSameSku = false,
  isInternational = false,
  deliveryDates,
  nextAvailableDifference,
  isSmartBuyUser,
  productUrl,
} = {}) => {
  const { trackEvent } = useAnalytics();
  const pathname = usePathname();

  const buildPayload = useCallback(
    (overrides = {}) =>
      buildCustomizationCleverTapPayload({
        baseData: baseCleverTapData,
        currentItem,
        isOutOfStock,
        isSameSku,
        isInternational,
        deliveryDates,
        nextAvailableDateDiff: nextAvailableDifference?.nextAvailableDateDiff ?? "",
        productUrl: productUrl || pathname,
        isSmartBuyUser,
        ...overrides,
      }),
    [
      baseCleverTapData,
      currentItem,
      deliveryDates,
      isInternational,
      isOutOfStock,
      isSameSku,
      isSmartBuyUser,
      nextAvailableDifference?.nextAvailableDateDiff,
      pathname,
      productUrl,
    ],
  );

  const trackCustomizeNowClicked = useCallback(() => {
    trackEvent(cleverTapCommonEvents.customizeNowClicked, buildPayload({ includeUrls: true }), ["clevertap"]);
  }, [buildPayload, trackEvent]);

  const trackCustomizationDone = useCallback(() => {
    trackEvent(cleverTapCommonEvents.customizationDone, buildPayload({ includePersonalizationStats: true }), [
      "clevertap",
    ]);
  }, [buildPayload, trackEvent]);

  return {
    trackCustomizeNowClicked,
    trackCustomizationDone,
    buildPayload,
  };
};

export default useCustomizationCleverTap;
export { buildCustomizationCleverTapPayload };
